import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  styled,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import LayersIcon from "@mui/icons-material/Layers";
import BuildIcon from "@mui/icons-material/Build";
import AddIcon from "@mui/icons-material/Add";

import {
  fetchGetAllServiciosBase,
  fetchAddServicioBase,
  fetchUpdateServicioBase,
  fetchDeleteServicioBase,
} from "../../action";
import "../../css/styles.css";

const isMobile =
  !!navigator.userAgent.match(/iphone|android|blackberry/gi) || false;

const StyledTableHead = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#00a62b",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const ListadoServiciosBase = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [filtro, setFiltro] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  // Dialog state for create / edit
  const [openModal, setOpenModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [nombreServicio, setNombreServicio] = useState("");

  const serviciosBase = useSelector((state) => state.app.itemServicios || []);

  const cargarDatos = async () => {
    setIsLoading(true);
    await dispatch(fetchGetAllServiciosBase());
    setIsLoading(false);
  };

  useEffect(() => {
    cargarDatos();
  }, [dispatch]);

  const handleOpenAddModal = () => {
    setEditingItem(null);
    setNombreServicio("");
    setOpenModal(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingItem(item);
    setNombreServicio(item.nombre || "");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingItem(null);
    setNombreServicio("");
  };

  const handleSaveServicio = async () => {
    if (!nombreServicio.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Ingrese el nombre del servicio",
      });
      return;
    }

    try {
      Swal.showLoading();
      if (editingItem) {
        // Update
        const payload = {
          ...editingItem,
          nombre: nombreServicio.trim(),
        };
        await dispatch(fetchUpdateServicioBase(payload));
        await dispatch(fetchGetAllServiciosBase());
        Swal.fire({
          icon: "success",
          title: "Servicio actualizado",
          text: "El servicio ha sido actualizado exitosamente",
        });
      } else {
        // Add
        const payload = {
          nombre: nombreServicio.trim(),
        };
        await dispatch(fetchAddServicioBase(payload));
        await dispatch(fetchGetAllServiciosBase());
        Swal.fire({
          icon: "success",
          title: "Servicio creado",
          text: "El servicio ha sido agregado exitosamente",
        });
      }
      handleCloseModal();
    } catch (err) {
      console.error("Error al guardar servicio", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al procesar el servicio",
      });
    }
  };

  const handleDeleteServicio = (e, row) => {
    e.stopPropagation();

    Swal.fire({
      title: `Eliminar Servicio`,
      text: `¿Confirma que desea eliminar el servicio "${row.nombre}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed || result.value) {
        Swal.showLoading();
        try {
          await dispatch(fetchDeleteServicioBase(row.id));
          await dispatch(fetchGetAllServiciosBase());
          Swal.fire({
            icon: "success",
            title: "Servicio eliminado",
            text: "El servicio ha sido eliminado exitosamente",
          });
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Ocurrió un error al eliminar el servicio",
          });
        }
      }
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFiltro = (e) => {
    setFiltro(e.target.value);
    setPage(0);
  };

  const filteredItems = (serviciosBase || []).filter((item) => {
    if (!filtro) return true;
    const search = filtro.toLowerCase();
    const nombre = item.nombre ? item.nombre.toLowerCase() : "";
    return nombre.includes(search);
  });

  const displayedItems =
    rowsPerPage > 0
      ? filteredItems.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      )
      : filteredItems;

  return (
    <div className="contenido">
      <div className="header">
        <h2>Servicios</h2>
      </div>

      {/* Navigation tabs within Planes module */}
      <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
        <Grid item>
          <Button
            variant="outlined"
            color="success"
            onClick={() => history.push("/planes")}
          >
            Planes
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="success"
            onClick={() => history.push("/planes/servicios")}
            sx={{ fontWeight: "bold" }}
          >
            Servicios
          </Button>
        </Grid>
      </Grid>

      <Grid container sx={{ mt: 1, mb: 2 }} justifyContent="space-between" alignItems="center">
        <Grid item xs={12} sm={6} md={4} className="alignLeft">
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleOpenAddModal}
            sx={{ fontWeight: "bold" }}
          >
            Agregar Servicio
          </Button>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className="alignLeft">
          <div style={{ position: "relative", width: "100%" }}>
            <input
              type="text"
              placeholder="Buscar servicio..."
              className="buscar"
              onChange={handleFiltro}
              value={filtro}
            />
            <SearchIcon style={{ position: "absolute", right: 10, top: 10, color: "#888" }} />
          </div>
        </Grid>
      </Grid>

      <div className="main">
        {isLoading && (
          <Grid container justifyContent="center" sx={{ my: 4 }}>
            <CircularProgress color="success" />
          </Grid>
        )}

        {!isLoading && (
          <Paper sx={{ width: "100%", overflow: "hidden", marginBottom: "2rem" }}>
            <TableContainer>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <StyledTableHead align="left">Nombre del Servicio</StyledTableHead>
                    <StyledTableHead align="center" style={{ width: "150px" }}>
                      Acciones
                    </StyledTableHead>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No se han encontrado servicios registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedItems.map((row, idx) => (
                      <TableRow
                        key={row.id || idx}
                        className={idx % 2 === 0 ? "filaOscura" : ""}
                      >
                        <TableCell align="left" style={{ fontWeight: 500 }}>
                          {row.nombre}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar" aria-label="editar">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleOpenEditModal(row)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Eliminar" aria-label="eliminar">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={(e) => handleDeleteServicio(e, row)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 20]}
              SelectProps={{
                native: true,
              }}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        )}
      </div>

      {/* Dialog to Add / Edit Servicio */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingItem ? "Editar Servicio" : "Agregar Servicio"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Nombre del Servicio"
              value={nombreServicio}
              onChange={(e) => setNombreServicio(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleSaveServicio}
            variant="contained"
            color="success"
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ListadoServiciosBase;
